/** 用户端 API：追问对话 / 六爻 / 钱包 / 配额 */
import { api, postStream } from './api';

// ============================================================
// Chat
// ============================================================
export interface ChatSessionSummary {
  id: string;
  title: string | null;
  chartId: string | null;
  totalCostYuan: string;
  createdAt: string;
  updatedAt: string;
  lastMessageStatus?: 'streaming' | 'completed';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  modelCode?: string | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
}

export interface ChatSessionDetail extends ChatSessionSummary {
  messages: ChatMessage[];
  selectedSchool: string | null;
  rollingSummary: string | null;
}

export const chatApi = {
  createSession: (input: {
    chartId?: string;
    fullReportId?: string;
    calibrationId?: string;
    title?: string;
  }) => api.post<ChatSessionSummary>('/chat/sessions', input),
  list: () => api.get<ChatSessionSummary[]>('/chat/sessions'),
  detail: (id: string) => api.get<ChatSessionDetail>(`/chat/sessions/${id}`),
  ask: (id: string, userMessage: string, overrideModelCode?: string) =>
    api.post<{
      messageId: string;
      content: string;
      modelCode: string;
      scene: string;
      usage: { inputTokens: number; outputTokens: number; costYuan: number; latencyMs: number };
      quota: { used: number; remaining: number; unlimited: boolean };
    }>(`/chat/sessions/${id}/ask`, { userMessage, overrideModelCode }),
  /** 流式追问 */
  askStream: (
    id: string,
    body: { userMessage: string; overrideModelCode?: string },
    handlers: {
      onDelta: (text: string) => void;
      onDone: (meta: Record<string, unknown>) => void;
      onError?: (err: Error) => void;
    },
    signal?: AbortSignal,
  ) =>
    postStream(
      `/chat/sessions/${id}/ask-stream`,
      body,
      {
        onEvent: (ev, data) => {
          if (ev === 'delta' && data && typeof data === 'object' && 'delta' in data) {
            handlers.onDelta(String((data as { delta: string }).delta));
          } else if (ev === 'done') {
            handlers.onDone((data as Record<string, unknown>) || {});
          } else if (ev === 'error') {
            handlers.onError?.(new Error(JSON.stringify(data)));
          }
        },
        onError: handlers.onError,
      },
      { signal },
    ),
};

// ============================================================
// Divination
// ============================================================
export interface DivinationCastResult {
  id: string;
  tosses: number[];
  benGua: { name: string; symbol: string; upper: string; lower: string };
  bianGua: { name: string; symbol: string };
  huGua: { name: string; symbol: string };
  dongYao: number[];
  quota: { used: number; remaining: number; unlimited: boolean };
}

export interface DivinationDetail {
  id: string;
  question: string;
  status: 'pending' | 'cast' | 'interpreted' | 'failed';
  benGua: unknown;
  bianGua: unknown;
  huGua: unknown;
  dongYao: number[];
  tosses: number[];
  interpretation: string | null;
  createdAt: string;
}

export const divinationApi = {
  cast: (question: string, chartId?: string) =>
    api.post<DivinationCastResult>('/divination/cast', { question, chartId }),
  list: () =>
    api.get<
      Array<{
        id: string;
        question: string;
        status: string;
        createdAt: string;
        benGua: { name: string };
      }>
    >('/divination'),
  detail: (id: string) => api.get<DivinationDetail>(`/divination/${id}`),
  interpret: (
    id: string,
    handlers: {
      onDelta: (text: string) => void;
      onDone: (meta: Record<string, unknown>) => void;
      onError?: (err: Error) => void;
    },
    signal?: AbortSignal,
  ) =>
    postStream(
      `/divination/${id}/interpret`,
      {},
      {
        onEvent: (ev, data) => {
          if (ev === 'delta' && data && typeof data === 'object' && 'delta' in data) {
            handlers.onDelta(String((data as { delta: string }).delta));
          } else if (ev === 'done') {
            handlers.onDone((data as Record<string, unknown>) || {});
          } else if (ev === 'error') {
            handlers.onError?.(new Error(JSON.stringify(data)));
          }
        },
        onError: handlers.onError,
      },
      { signal },
    ),
};

// ============================================================
// Wallet
// ============================================================
export interface WalletInfo {
  id: string;
  balanceYuan: string;
  totalRechargeYuan: string;
  totalSpendYuan: string;
}
export interface WalletTxn {
  id: string;
  type: string;
  amountYuan: string;
  balanceAfter: string;
  remark: string | null;
  createdAt: string;
}
export interface RechargePackage {
  id: string;
  code: string;
  name: string;
  priceYuan: string;
  creditYuan: string;
  recommended: boolean;
  sortOrder: number;
}

export const walletApi = {
  me: () => api.get<WalletInfo>('/wallet/me'),
  transactions: (page = 1, pageSize = 20) =>
    api.get<{ list: WalletTxn[]; total: number; page: number; pageSize: number }>(
      `/wallet/me/transactions?page=${page}&pageSize=${pageSize}`,
    ),
  packages: () => api.get<RechargePackage[]>('/wallet/packages'),
};

// ============================================================
// Quota
// ============================================================
export interface QuotaStatus {
  ok: boolean;
  used: number;
  remaining: number;
  unlimited: boolean;
  resetCycle: string | null;
  reason?: string;
}
export const quotaApi = {
  query: (keys: string[]) =>
    api.get<Record<string, QuotaStatus>>(`/quotas?keys=${encodeURIComponent(keys.join(','))}`),
};
